import { useState, useEffect } from "react";
import {
    TextField,
    Button,
    IconButton,
    Tooltip,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    IconButton as MuiIconButton,
    TablePagination,
    Typography,
    Chip,
    Stack,
    Fade,
    Grid,
    InputAdornment,
    LinearProgress,
    Divider,
    useMediaQuery,
    useTheme,
    Skeleton,
    MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InventoryIcon from "@mui/icons-material/Inventory2Outlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import TagOutlinedIcon from "@mui/icons-material/TagOutlined";
import AxiosWrapper from "../../../basecomp/base/AxiosWrapper";
import { fetchSlocOptions } from "../model/PrintingModel";
// import { fetchSlocOptions } from "../../../services/materialSearchService"; // adjust path as needed

// ---- Brand tokens (kept consistent with the rest of GSOne) ----
const BRAND = {
    primary: "#0b3473",
    primaryDark: "#092b5f",
    primarySoft: "#eef2ff",
    primarySofter: "#f7f8fa",
    border: "#e6e8ec",
    borderSoft: "#eef0f3",
    textMuted: "#5b6472",
};

const EMPTY_FILTERS = {
    localMaterialNumber: "",
    materialDescription: "",
    manufactureName: "",
    manufacturePartNumber: "",
    sLoc: "",
    binLocation: "",
};

// Color coding for ABC classification chips.
const getAbcStyle = (abcClass) => {
    const key = (abcClass || "").toString().trim().toUpperCase();
    switch (key) {
        case "A":
            return { bg: "#e7f7ee", color: "#1a7f4b" };
        case "B":
            return { bg: "#fff4e0", color: "#b06a00" };
        case "C":
            return { bg: "#f1f2f4", color: "#54606e" };
        default:
            return { bg: "#f1f2f4", color: "#54606e" };
    }
};

const FILTER_FIELDS = [
    { name: "localMaterialNumber", label: "Local Material Number" },
    { name: "materialDescription", label: "Material Description" },
    { name: "manufactureName", label: "Manufacture Name" },
    { name: "manufacturePartNumber", label: "Manufacture Part Number" },
    { name: "binLocation", label: "Bin Location" },
];

// Small reusable "empty state" label — replaces bare "-" everywhere.
const NotPresent = () => (
    <Typography
        component="span"
        variant="body2"
        sx={{ fontStyle: "italic", color: "text.disabled" }}
    >
        Not present
    </Typography>
);

const MaterialSearch = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isXs = useMediaQuery(theme.breakpoints.down("sm"));

    const [filters, setFilters] = useState(EMPTY_FILTERS);

    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
    const [limit, setLimit] = useState(20);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [activeImages, setActiveImages] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);

    const [slocOptions, setSlocOptions] = useState([]);

    const activeFilterCount = Object.values(filters).filter((v) => v.trim() !== "").length;

    useEffect(() => {
        const loadSlocOptions = async () => {
            try {
                const response = await fetchSlocOptions();
                setSlocOptions(response?.data?.options || []);
            } catch (error) {
                console.error("Failed to fetch SLoc options:", error);
                setSlocOptions([]);
            }
        };
        loadSlocOptions();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") handleSearch();
    };

    const fetchData = async (filtersToUse, pageToFetch = page, limitToFetch = limit) => {
        try {
            setLoading(true);

            const activeFilters = Object.fromEntries(
                Object.entries(filtersToUse).filter(([, value]) => value.trim() !== "")
            );

            const params = {
                ...activeFilters,
                page: pageToFetch + 1, // backend is 1-indexed
                limit: limitToFetch,
            };

            const Base_URL = process.env.REACT_APP_BE_URL;

            const response = await AxiosWrapper.axiosGetCallWithToken(
                `${Base_URL}/label_print/materialSearch`,
                params
            );

            const payload = response?.data;

            if (!payload || !Array.isArray(payload.data)) {
                setRows([]);
                setTotalCount(0);
                return;
            }

            setRows(payload.data);
            setTotalCount(payload.totalCount || 0);
        } catch (error) {
            console.error("Search failed:", error);
            setRows([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        // Capture the current filter values before clearing, so the search
        // still runs against what the user typed.
        const filtersForSearch = filters;
        setPage(0);
        setHasSearched(true);
        fetchData(filtersForSearch, 0, limit);
    };

    const handleReset = () => {
        setFilters(EMPTY_FILTERS);
        setRows([]);
        setTotalCount(0);
        setPage(0);
        setHasSearched(false);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        fetchData(filters, newPage, limit);
    };

    const handleChangeRowsPerPage = (event) => {
        const newLimit = parseInt(event.target.value, 10);
        setLimit(newLimit);
        setPage(0);
        fetchData(filters, 0, newLimit);
    };

    const openImageModal = (images, startIndex = 0) => {
        setActiveImages(images || []);
        setActiveIndex(startIndex);
        setImageModalOpen(true);
    };

    const closeImageModal = () => {
        setImageModalOpen(false);
        setActiveImages([]);
        setActiveIndex(0);
    };

    const showPrevImage = () => {
        setActiveIndex((prev) => (prev === 0 ? activeImages.length - 1 : prev - 1));
    };

    const showNextImage = () => {
        setActiveIndex((prev) => (prev === activeImages.length - 1 ? 0 : prev + 1));
    };

    // Backend sends images as e.g.:
    //   ["{\"https://.../a.jpg\",\"https://.../b.jpg\"}"]
    // i.e. a JS array containing ONE Postgres array-literal string, not a
    // plain array of URLs. Also tolerate a genuinely plain array of URLs
    // (in case that ever changes) and a single bare URL string.
    const parseImageLinks = (raw) => {
        if (!raw) return [];
        const items = Array.isArray(raw) ? raw : [raw];
        const urls = [];

        items.forEach((item) => {
            if (typeof item !== "string") return;
            const trimmed = item.trim();

            if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
                // Postgres array literal: {"url1","url2",...}
                const inner = trimmed.slice(1, -1);
                const matches = inner.match(/"(?:[^"\\]|\\.)*"|[^,]+/g) || [];
                matches.forEach((m) => {
                    let cleaned = m.trim();
                    if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
                        cleaned = cleaned.slice(1, -1);
                    }
                    cleaned = cleaned.replace(/\\"/g, '"').trim();
                    if (cleaned) urls.push(cleaned);
                });
            } else if (trimmed) {
                urls.push(trimmed);
            }
        });

        return urls;
    };

    // Build "name / number" pairs from mpn_name1..5 / mpn_number1..5
    const getMpnList = (row) => {
        const pairs = [];
        for (let i = 1; i <= 5; i++) {
            const name = row[`mpn_name${i}`];
            const number = row[`mpn_number${i}`];
            if (name || number) {
                pairs.push([name, number].filter(Boolean).join(" / "));
            }
        }
        if (row["manufacturer_name"] || row["manufac_item_code"]) {
            pairs.push(
                [row["manufacturer_name"], row["manufac_item_code"]].filter(Boolean).join(" / ")
            );
        }
        return pairs;
    };

    // Build "storage_location / bin_location" list from storage_locations array
    const getLocationList = (row) => {
        if (!Array.isArray(row.storage_locations)) return [];

        return row.storage_locations
            .filter(
                (loc) =>
                    loc.storage_location ||
                    loc.bin_location ||
                    (loc.ohq !== null && loc.ohq !== undefined && loc.ohq !== "")
            )
            .map((loc) =>
                [
                    loc.storage_location || "-",
                    loc.bin_location || "-",
                    loc.ohq !== null && loc.ohq !== undefined && loc.ohq !== ""
                        ? loc.ohq
                        : "-"
                ].join(" / ")
            );
    };

    // ---------- Sub-renders ----------

    const renderFilterPanel = () => (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, sm: 2.5, md: 3 },
                borderRadius: "16px",
                border: `1px solid ${BRAND.border}`,
                boxShadow: "0 2px 12px rgba(16, 24, 40, 0.05)",
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2.5 }}
            >
                <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Box
                        sx={{
                            width: 34,
                            height: 34,
                            borderRadius: "10px",
                            backgroundColor: BRAND.primarySoft,
                            color: BRAND.primary,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <InventoryIcon fontSize="small" />
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#1f2937" }}>
                        Material Search
                    </Typography>
                </Stack>

                {activeFilterCount > 0 && (
                    <Chip
                        size="small"
                        label={`${activeFilterCount} filter${activeFilterCount > 1 ? "s" : ""} active`}
                        sx={{
                            backgroundColor: BRAND.primarySoft,
                            color: BRAND.primary,
                            fontWeight: 600,
                            display: { xs: "none", sm: "flex" },
                        }}
                    />
                )}
            </Stack>

            <Grid container spacing={2}>
                {FILTER_FIELDS.map((field) => (
                    <Grid item xs={12} sm={6} md={4} key={field.name}>
                        <TextField
                            label={field.label}
                            name={field.name}
                            value={filters[field.name]}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                            placeholder="Enter value"
                            fullWidth
                            size="small"
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                    backgroundColor: "#fff",
                                },
                            }}
                        />
                    </Grid>
                ))}

                {/* SLoc dropdown — replaces old Equipment Function text field */}
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        select
                        label="Storage Location"
                        name="sLoc"
                        value={filters.sLoc || ""}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                                backgroundColor: "#fff",
                            },
                        }}
                    >
                        <MenuItem value="">
                            <em>Clear selection</em>
                        </MenuItem>

                        {slocOptions.map((option) => (
                            <MenuItem
                                key={option.value ?? option}
                                value={option.value ?? option}
                            >
                                {option.label ?? option}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
            </Grid>

            <Divider sx={{ my: 2.5 }} />

            <Stack
                direction={{ xs: "column-reverse", sm: "row" }}
                spacing={1.5}
                justifyContent="flex-end"
            >
                <Button
                    onClick={handleReset}
                    startIcon={<RefreshIcon />}
                    fullWidth={isXs}
                    sx={{
                        color: BRAND.primary,
                        backgroundColor: BRAND.primarySofter,
                        borderRadius: "10px",
                        px: 2.5,
                        py: 1,
                        fontWeight: 600,
                        textTransform: "none",
                        "&:hover": { backgroundColor: "#e3e6eb" },
                    }}
                >
                    Reset
                </Button>
                <Button
                    onClick={handleSearch}
                    startIcon={<SearchIcon />}
                    fullWidth={isXs}
                    sx={{
                        backgroundColor: BRAND.primary,
                        color: "white",
                        borderRadius: "10px",
                        px: 3,
                        py: 1,
                        fontWeight: 600,
                        textTransform: "none",
                        boxShadow: "0 4px 12px rgba(11, 52, 115, 0.25)",
                        "&:hover": { backgroundColor: BRAND.primaryDark },
                    }}
                >
                    Search
                </Button>
            </Stack>
        </Paper>
    );

    const renderResultsHeader = () => (
        <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 1.5 }}
        >
            <Typography sx={{ fontWeight: 600, color: "#1f2937" }}>
                {totalCount > 0
                    ? `${totalCount} material${totalCount === 1 ? "" : "s"} found`
                    : "Results"}
            </Typography>
        </Stack>
    );

    const renderLoadingSkeleton = () => (
        <Stack spacing={1.25}>
            {Array.from({ length: isMobile ? 3 : 5 }).map((_, i) => (
                <Skeleton
                    key={i}
                    variant="rounded"
                    height={isMobile ? 120 : 56}
                    sx={{ borderRadius: "12px" }}
                />
            ))}
        </Stack>
    );

    const renderEmptyState = () => (
        <Box
            sx={{
                mt: 3,
                py: { xs: 5, sm: 7 },
                px: 2,
                textAlign: "center",
                borderRadius: "16px",
                border: `1px dashed ${BRAND.border}`,
                backgroundColor: BRAND.primarySofter,
            }}
        >
            <SearchOffIcon sx={{ fontSize: 40, color: BRAND.textMuted, mb: 1 }} />
            <Typography sx={{ fontWeight: 600, color: "#1f2937" }}>
                {hasSearched ? "No materials matched your search" : "No results yet"}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                {hasSearched
                    ? "Try broadening your filters or check for typos."
                    : "Enter one or more filters above and press Search."}
            </Typography>
        </Box>
    );

    // Small compact section used inside the card — blue caption label, tight spacing,
    // no icons and no divider lines (keeps the card dense instead of airy).
    const CardSection = ({ label, children, sx }) => (
        <Box sx={{ mt: 1, ...sx }}>
            <Typography
                variant="caption"
                sx={{
                    color: BRAND.primary,
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    letterSpacing: "0.02em",
                    display: "block",
                    mb: 0.35,
                }}
            >
                {label}
            </Typography>
            {children}
        </Box>
    );

    // Mobile / tablet card list — replaces the table below the "md" breakpoint.
    const renderCardList = () => (
        <Stack spacing={1.25}>
            {rows.map((row, rIdx) => {
                const mpnList = getMpnList(row);
                const locationList = getLocationList(row);
                const imageList = parseImageLinks(row.images);
                const hasImages = imageList.length > 0;
                const abcStyle = getAbcStyle(row.abc_class);

                return (
                    <Paper
                        key={row.id ?? rIdx}
                        elevation={0}
                        sx={{
                            borderRadius: "14px",
                            border: `1px solid ${BRAND.border}`,
                            boxShadow: "0 1px 6px rgba(16, 24, 40, 0.04)",
                            overflow: "hidden",
                            p: 1.5,
                        }}
                    >
                        {/* Material No + ABC Class on one line — no separate block needed */}
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="caption"
                                    sx={{ color: BRAND.primary, fontWeight: 700, fontSize: "0.68rem", letterSpacing: "0.02em", display: "block", mb: 0.15 }}
                                >
                                    Material No
                                </Typography>
                                <Typography sx={{ fontWeight: 700, color: "#1f2937", fontSize: "1.05rem", lineHeight: 1.25 }}>
                                    {row.material_no || <NotPresent />}
                                </Typography>
                            </Box>

                        </Stack>

                        <Typography
                            variant="body2"
                            sx={{
                                mt: 0.85,
                                color: "#374151",
                                lineHeight: 1.45,
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                            }}
                        >
                            {row.material_desc || <NotPresent />}
                        </Typography>

                        <CardSection label="MPN (Name / Number)">
                            {mpnList.length > 0 ? (
                                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                    {mpnList.map((mpn, idx) => (
                                        <Chip
                                            key={idx}
                                            label={mpn}
                                            size="small"
                                            sx={{
                                                backgroundColor: BRAND.primarySofter,
                                                color: "#374151",
                                                fontWeight: 500,
                                                fontSize: "0.7rem",
                                            }}
                                        />
                                    ))}
                                </Stack>
                            ) : <NotPresent />}
                        </CardSection>

                        {/* Storage Loc / Bin Location + UOM together on one line — values are short */}
                        <CardSection label="Storage Loc / Bin Location / OHQ">
                            {locationList.length > 0 ? (
                                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                    {locationList.map((loc, idx) => (
                                        <Chip
                                            key={idx}
                                            label={loc}
                                            size="small"
                                            sx={{
                                                backgroundColor: BRAND.primarySoft,
                                                color: BRAND.primary,
                                                fontWeight: 500,
                                                fontSize: "0.7rem",
                                            }}
                                        />
                                    ))}
                                </Stack>
                            ) : (
                                <NotPresent />
                            )}
                        </CardSection>

                        <Stack direction="row" spacing={3}>
                            <CardSection label="UOM" sx={{ flex: 1 }}>
                                {row.b_un ? (
                                    <Chip
                                        label={row.b_un}
                                        size="small"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "0.7rem",
                                            backgroundColor: "transparent",
                                            color: "#54606e",
                                        }}
                                    />
                                ) : (
                                    <NotPresent />
                                )}
                            </CardSection>

                            <CardSection label="ABC Class" sx={{ flex: 1 }}>
                                {row.abc_class ? (
                                    <Chip
                                        size="small"
                                        label={row.abc_class}
                                        sx={{
                                            backgroundColor: "transparent",
                                            color: "#54606e",
                                            fontWeight: 700,
                                            fontSize: "0.7rem",
                                        }}
                                    />
                                ) : (
                                    <NotPresent />
                                )}
                            </CardSection>
                        </Stack>

                        <Button
                            fullWidth
                            disabled={!hasImages}
                            onClick={() => {
                                if (hasImages) {
                                    openImageModal(imageList, 0);
                                }
                            }}
                            startIcon={
                                hasImages ? (
                                    <Box
                                        component="img"
                                        src={imageList[0]}
                                        alt=""
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: "5px",
                                            objectFit: "cover",
                                            border: `1px solid ${BRAND.border}`,
                                        }}
                                    />
                                ) : (
                                    <ImageIcon />
                                )
                            }
                            sx={{
                                mt: 1.25,
                                borderRadius: "10px",
                                py: 0.85,
                                fontWeight: 600,
                                textTransform: "none",
                                justifyContent: "center",

                                ...(hasImages
                                    ? {
                                        backgroundColor: BRAND.primarySoft,
                                        color: BRAND.primary,
                                        "&:hover": {
                                            backgroundColor: "#dfe6fb",
                                        },
                                    }
                                    : {
                                        backgroundColor: "#f3f4f6",
                                        color: "#9ca3af",
                                    }),
                            }}
                        >
                            {hasImages
                                ? `View ${imageList.length} Image${imageList.length > 1 ? "s" : ""}`
                                : "No Images"}
                        </Button>
                    </Paper>
                );
            })}
        </Stack>
    );

    // Desktop / laptop table view.
    const renderTable = () => (
        <TableContainer
            component={Paper}
            elevation={0}
            sx={{
                borderRadius: "14px",
                border: `1px solid ${BRAND.border}`,
                overflow: "hidden",
                boxShadow: "0 2px 10px rgba(16, 24, 40, 0.04)",
            }}
        >
            <Table size="small">
                <TableHead>
                    <TableRow
                        sx={{
                            "& th": {
                                fontWeight: 700,
                                fontSize: "0.78rem",
                                letterSpacing: "0.03em",
                                textTransform: "uppercase",
                                color: BRAND.textMuted,
                                backgroundColor: BRAND.primarySofter,
                                borderBottom: `1px solid ${BRAND.border}`,
                                py: 1.5,
                            },
                        }}
                    >
                        <TableCell>Material No</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>MPN (Name / Number)</TableCell>
                        <TableCell>Storage Loc / Bin Location / OHQ</TableCell>
                        <TableCell>UOM</TableCell>
                        <TableCell>ABC Class</TableCell>
                        <TableCell align="center">Images</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, rIdx) => {
                        const mpnList = getMpnList(row);
                        const locationList = getLocationList(row);
                        const imageList = parseImageLinks(row.images);
                        const hasImages = imageList.length > 0;

                        return (
                            <TableRow
                                key={row.id ?? rIdx}
                                hover
                                sx={{
                                    "& td": {
                                        borderBottom: `1px solid ${BRAND.borderSoft}`,
                                        py: 1.1,
                                        verticalAlign: "top",
                                    },
                                    "&:last-child td": { borderBottom: "none" },
                                }}
                            >
                                <TableCell sx={{ fontWeight: 600, color: "#1f2937" }}>
                                    {row.material_no || <NotPresent />}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        maxWidth: 320,
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                        overflowWrap: "anywhere",
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {row.material_desc || <NotPresent />}
                                </TableCell>
                                <TableCell>
                                    {mpnList.length > 0 ? (
                                        <Stack spacing={0.5}>
                                            {mpnList.map((mpn, idx) => (
                                                <Typography
                                                    key={idx}
                                                    variant="body2"
                                                    sx={{ whiteSpace: "nowrap" }}
                                                >
                                                    {mpn}
                                                </Typography>
                                            ))}
                                        </Stack>
                                    ) : (
                                        <NotPresent />
                                    )}
                                </TableCell>
                                <TableCell>
                                    {locationList.length > 0 ? (
                                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                            {locationList.map((loc, idx) => (
                                                <Chip
                                                    key={idx}
                                                    label={loc}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: BRAND.primarySoft,
                                                        color: BRAND.primary,
                                                        fontWeight: 500,
                                                        fontSize: "0.72rem",
                                                    }}
                                                />
                                            ))}
                                        </Stack>
                                    ) : (
                                        <NotPresent />
                                    )}
                                </TableCell>
                                <TableCell>{row.b_un || <NotPresent />}</TableCell>
                                <TableCell>{row.abc_class || <NotPresent />}</TableCell>
                                <TableCell align="center">
                                    {hasImages ? (
                                        <Tooltip title={`View ${imageList.length} image(s)`}>
                                            <IconButton
                                                size="small"
                                                onClick={() => openImageModal(imageList, 0)}
                                                sx={{
                                                    color: BRAND.primary,
                                                    backgroundColor: BRAND.primarySoft,
                                                    "&:hover": { backgroundColor: "#dfe6fb" },
                                                }}
                                            >
                                                <ImageIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        <NotPresent />
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <Box
            sx={{
                marginTop: { xs: "5em", sm: "6em" },
                padding: { xs: "0.75rem", sm: "1.25rem", md: "1.5rem" },
                maxWidth: "1400px",
                mx: "auto",
            }}
        >
            {renderFilterPanel()}

            <Box sx={{ marginTop: { xs: "1.5rem", sm: "2rem" } }}>
                {loading && renderLoadingSkeleton()}

                {!loading && rows.length > 0 && (
                    <>
                        {renderResultsHeader()}

                        {/* Table on md+ screens */}
                        <Box sx={{ display: { xs: "none", md: "block" } }}>{renderTable()}</Box>

                        {/* Card list on mobile / tablet */}
                        <Box sx={{ display: { xs: "block", md: "none" } }}>{renderCardList()}</Box>

                        <Box
                            sx={{
                                mt: 1,
                                borderRadius: "12px",
                                border: `1px solid ${BRAND.border}`,
                                overflow: "hidden",
                            }}
                        >
                            <TablePagination
                                component="div"
                                count={totalCount}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={limit}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[10, 20, 50, 100]}
                                sx={{
                                    ".MuiTablePagination-toolbar": {
                                        flexWrap: { xs: "wrap", sm: "nowrap" },
                                        justifyContent: "center",
                                        rowGap: 0.5,
                                    },
                                }}
                            />
                        </Box>
                    </>
                )}

                {!loading && rows.length === 0 && renderEmptyState()}
            </Box>

            {/* Image Carousel Modal */}
            <Dialog
                open={imageModalOpen}
                onClose={closeImageModal}
                maxWidth="md"
                fullWidth
                fullScreen={isXs}
                TransitionComponent={Fade}
                PaperProps={{
                    sx: {
                        borderRadius: isXs ? 0 : "16px",
                        backgroundColor: "#0d1117",
                        overflow: "hidden",
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        px: 2,
                        py: 1.5,
                    }}
                >
                    <Typography sx={{ color: "#e5e7eb", fontWeight: 600 }}>
                        {activeImages.length > 0
                            ? `Image ${activeIndex + 1} of ${activeImages.length}`
                            : "Material Images"}
                    </Typography>
                    <MuiIconButton onClick={closeImageModal} sx={{ color: "#e5e7eb" }}>
                        <CloseIcon />
                    </MuiIconButton>
                </Box>

                <Box
                    sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: { xs: "60vh", sm: "420px" },
                        backgroundColor: "#000",
                    }}
                >
                    {activeImages.length > 1 && (
                        <MuiIconButton
                            onClick={showPrevImage}
                            sx={{
                                position: "absolute",
                                left: 12,
                                color: "#fff",
                                backgroundColor: "rgba(255,255,255,0.08)",
                                "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
                            }}
                        >
                            <ChevronLeftIcon />
                        </MuiIconButton>
                    )}

                    {activeImages[activeIndex] && (
                        <img
                            src={activeImages[activeIndex]}
                            alt={`material-img-${activeIndex}`}
                            style={{
                                maxHeight: isXs ? "60vh" : "70vh",
                                maxWidth: "100%",
                                objectFit: "contain",
                                borderRadius: "4px",
                            }}
                        />
                    )}

                    {activeImages.length > 1 && (
                        <MuiIconButton
                            onClick={showNextImage}
                            sx={{
                                position: "absolute",
                                right: 12,
                                color: "#fff",
                                backgroundColor: "rgba(255,255,255,0.08)",
                                "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
                            }}
                        >
                            <ChevronRightIcon />
                        </MuiIconButton>
                    )}
                </Box>

                {activeImages.length > 1 && (
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ p: 1.5, overflowX: "auto", backgroundColor: "#0d1117" }}
                    >
                        {activeImages.map((img, idx) => (
                            <Box
                                key={idx}
                                component="img"
                                src={img}
                                alt={`thumb-${idx}`}
                                onClick={() => setActiveIndex(idx)}
                                sx={{
                                    width: 56,
                                    height: 56,
                                    objectFit: "cover",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    flexShrink: 0,
                                    border:
                                        idx === activeIndex
                                            ? "2px solid #4f7dfa"
                                            : "2px solid transparent",
                                    opacity: idx === activeIndex ? 1 : 0.6,
                                    transition: "opacity .15s ease, border-color .15s ease",
                                }}
                            />
                        ))}
                    </Stack>
                )}
            </Dialog>
        </Box>
    );
};

export default MaterialSearch;